'use client';

import { AccordionItem, Flex, Link, Typography } from '@coolblue-development/freddy';
import readPropertyInformationTrigger from '@coolblue-development/ga4-events/triggers/FilterAndSort/readPropertyInformationTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFilterDrawer } from '@features/filter/context';
import { useTranslation } from '@i18n/useTranslation';
import { Fragment } from 'react';

import type { Facet as FacetType } from '../../types';
import { isValidFilterType } from './helpers/isValidFilterType';
import mapFacetToFilterGroup from './helpers/mapFacetToFilterGroup';

export default function MobileFacet({ facet }: { facet: FacetType }) {
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation('webshop');
  const propertyExplanationLabel = t('filterPage.propertyExplanationLabel');
  const { name, facetName, displayName, propertyExplanationMetaData } = facet;
  const propertyName = `${name}, ${facetName}, ${propertyExplanationMetaData?.productTypeId}`;

  const { setPropertyExplanation } = useFilterDrawer();

  function handlePropertyExplanationToggle() {
    if (!propertyExplanationMetaData) {
      return;
    }

    setPropertyExplanation({
      property: {
        displayName,
        hasExplanation: Boolean(propertyExplanationMetaData),
        propertyId: String(propertyExplanationMetaData.propertyId)
      },
      productTypeId: String(propertyExplanationMetaData.productTypeId),
      title: displayName
    });

    trackEvent(
      readPropertyInformationTrigger({
        property_name: propertyName,
        sub_feature: 'filter_list'
      })
    );
  }

  if (isValidFilterType(facet.filterType)) {
    const selectedValues = facet.values
      ?.filter(value => value.isSelected)
      ?.map(value => value.displayName)
      .join(', ');

    const facets = mapFacetToFilterGroup(facet);

    return (
      <AccordionItem
        title={
          <Flex direction='column'>
            <Flex gap={200}>
              <Typography
                variant='h5'
                component='span'
                mb={0}
                truncateLines={1}
                overflowWrap='normal'
              >
                {displayName}
              </Typography>

              {propertyExplanationMetaData && (
                <Link component='button' onClick={handlePropertyExplanationToggle}>
                  {propertyExplanationLabel}
                </Link>
              )}
            </Flex>

            {selectedValues && (
              <Typography
                color='subtle'
                component='span'
                truncateLines={1}
                overflowWrap='normal'
                className='selected-filters'
              >
                {selectedValues}
              </Typography>
            )}
          </Flex>
        }
      >
        <Flex direction='column' alignItems='flex-start' gap={200}>
          {facets}
        </Flex>
      </AccordionItem>
    );
  }

  return <Fragment key={facetName} />;
}
